/**
 * Manager overview utilities: Seamless (`accountingSystems` entry with `name === "Seamless"`),
 * accounting rows, lots, strategy drill-down, and {@link buildManagersOverviewRows}.
 */
import { findStrategy } from "../../component/Methods";
import MonitorScreenCalculation from "../Portfolio/MonitorScreenCalculation";
import {
  extractFundId,
  extractSmaId,
  fundLabel,
  smaLabel,
} from "./popup/managerDetailDialogLabels";
import { compareLabelInsensitive } from "./popup/managerDetailDialogTableUtils";

function safeNum(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function aggregateManagerPeriodPnL({
  managerLots,
  accountingData,
  funds,
  strategies,
  marketData,
  trades,
  isStrategist,
  markets,
}) {
  if (!managerLots?.length) {
    return { week: 0, month: 0, quarter: 0, year: 0, days365: 0 };
  }
  const rows = MonitorScreenCalculation({
    selectedFund: undefined,
    selectedStrategy: undefined,
    selectedSide: "Both",
    lots: managerLots,
    accountingData: Array.isArray(accountingData) ? accountingData : [],
    funds: Array.isArray(funds) ? funds : [],
    strategies: Array.isArray(strategies) ? strategies : [],
    marketData: Array.isArray(marketData) ? marketData : [],
    trades: Array.isArray(trades) ? trades : [],
    isStrategist: Boolean(isStrategist),
    markets: Array.isArray(markets) ? markets : [],
  });
  if (!Array.isArray(rows) || rows.length === 0) {
    return { week: 0, month: 0, quarter: 0, year: 0, days365: 0 };
  }
  return rows.reduce(
    (acc, row) => {
      const y = safeNum(row.profitLossYear);
      const d365 =
        row.profitLoss365 != null && row.profitLoss365 !== "" ? safeNum(row.profitLoss365) : y;
      return {
        week: acc.week + safeNum(row.profitLossWeek),
        month: acc.month + safeNum(row.profitLossMonth),
        quarter: acc.quarter + safeNum(row.profitLossQuarter),
        year: acc.year + y,
        days365: acc.days365 + d365,
      };
    },
    { week: 0, month: 0, quarter: 0, year: 0, days365: 0 }
  );
}

// --- ids / shapes ----------------------------------------------------------

/** @param {Record<string, unknown> | unknown[] | undefined} funds */
function collectIdsFromFundsShape(funds, idKeys) {
  const ids = new Set();
  if (!funds) return ids;
  if (Array.isArray(funds)) {
    funds.forEach((item) => {
      if (typeof item === "string" && item) ids.add(String(item));
      else if (item && typeof item === "object") {
        for (const k of idKeys) {
          if (item[k]) {
            ids.add(String(item[k]));
            break;
          }
        }
      }
    });
    return ids;
  }
  if (typeof funds === "object") {
    Object.keys(funds).forEach((k) => {
      if (k) ids.add(String(k));
    });
    Object.values(funds).forEach((val) => {
      if (val && typeof val === "object") {
        for (const k of idKeys) {
          if (val[k]) {
            ids.add(String(val[k]));
            break;
          }
        }
      }
    });
  }
  return ids;
}

export function collectManagerEntityIds(manager) {
  const fundIds = collectIdsFromFundsShape(manager?.funds, ["fundId", "id", "fund"]);
  const smaIds = collectIdsFromFundsShape(manager?.smas, ["smaId", "fundId", "id", "sma"]);
  const allEntityIds = new Set([...fundIds, ...smaIds]);
  return { fundIds, smaIds, allEntityIds };
}

function countFundsShape(manager) {
  const f = manager?.funds;
  if (!f) return 0;
  if (Array.isArray(f)) return f.length;
  if (typeof f === "object") return Object.keys(f).length;
  return 0;
}

function countSmasShape(manager) {
  const s = manager?.smas;
  if (!s) return 0;
  if (Array.isArray(s)) return s.length;
  if (typeof s === "object") return Object.keys(s).length;
  return 0;
}

// --- accounting / Seamless (`accountingSystems[accountingId].name === "Seamless"`) ---

function getSeamlessAccountingSystem(accountingSystems) {
  if (!accountingSystems || typeof accountingSystems !== "object") return null;
  for (const accountingId of Object.keys(accountingSystems)) {
    const sys = accountingSystems[accountingId];
    if (sys && typeof sys === "object" && sys.name === "Seamless") return sys;
  }
  return null;
}

export function getSeamlessFundRowsForManager(accountingSystems, managerId, fundIdsOnly) {
  const sys = getSeamlessAccountingSystem(accountingSystems);
  if (!sys) return [];
  let fundsList = sys.funds;
  if (!fundsList) return [];
  if (typeof fundsList === "object" && !Array.isArray(fundsList)) {
    fundsList = Object.values(fundsList);
  }
  if (!Array.isArray(fundsList)) return [];
  const mid = managerId != null && managerId !== "" ? String(managerId) : "";
  return fundsList.filter((row) => {
    if (!row || typeof row !== "object") return false;
    const rid = row.managerId != null && row.managerId !== "" ? String(row.managerId) : "";
    if (mid && rid && rid !== mid) return false;
    const fundKey =
      row.fund != null && row.fund !== ""
        ? String(row.fund)
        : row.fundId != null && row.fundId !== ""
          ? String(row.fundId)
          : "";
    if (!fundKey) return false;
    if (fundIdsOnly?.size && !fundIdsOnly.has(fundKey)) return false;
    return true;
  });
}

export function mapSeamlessFundRowsByFundId(accountingSystems, managerId, fundIdsOnly) {
  const rows = getSeamlessFundRowsForManager(accountingSystems, managerId, fundIdsOnly);
  const m = new Map();
  for (const r of rows) {
    const id =
      r?.fund != null && r.fund !== ""
        ? String(r.fund)
        : r?.fundId != null && r.fundId !== ""
          ? String(r.fundId)
          : "";
    if (id && !m.has(id)) m.set(id, r);
  }
  return m;
}

export function accountingRowBelongsToManager(row, managerId, entityIds) {
  const fundKey =
    row?.fund != null && row.fund !== ""
      ? String(row.fund)
      : row?.fundId != null && row.fundId !== ""
        ? String(row.fundId)
        : "";
  const rowMid =
    row?.managerId != null && row.managerId !== "" ? String(row.managerId) : null;
  const mid = managerId != null && managerId !== "" ? String(managerId) : "";
  if (rowMid !== null && rowMid !== mid) return false;
  return rowMid === mid || entityIds.has(fundKey);
}

export function filterAccountingRowsForManager(rows, managerId, entityIds) {
  return rows.filter((row) => accountingRowBelongsToManager(row, managerId, entityIds));
}

export function getAccountingRowsForManagerOverview(manager, accountingRowsAll, managerId) {
  const { fundIds, smaIds, allEntityIds } = collectManagerEntityIds(manager);
  const legacyAll = filterAccountingRowsForManager(accountingRowsAll, managerId, allEntityIds);
  const seamlessFunds = getSeamlessFundRowsForManager(manager?.accountingSystems, managerId, fundIds);

  if (seamlessFunds.length > 0) {
    const legacySmaOnly = legacyAll.filter((row) => {
      const fk = row?.fund != null && row.fund !== "" ? String(row.fund) : "";
      return fk && smaIds.has(fk);
    });
    return [...seamlessFunds, ...legacySmaOnly];
  }
  return legacyAll;
}

/** @param {unknown[]} accountingData */
export function getAccountingFundRows(accountingData) {
  if (!Array.isArray(accountingData) || accountingData.length === 0) return [];
  const first = accountingData[0];
  if (first && typeof first === "object" && Array.isArray(first.funds)) return first.funds;
  return [];
}

export function sumAumFromRows(rows) {
  return rows.reduce((acc, row) => {
    const a = parseFloat(row?.aum);
    return acc + (Number.isFinite(a) ? a : 0);
  }, 0);
}

export function sumAumForEntity(rows, entityId) {
  if (!Array.isArray(rows) || entityId == null || entityId === "") return 0;
  const id = String(entityId);
  return rows.reduce((acc, row) => {
    const rowFund =
      row?.fund != null && row.fund !== ""
        ? String(row.fund)
        : row?.fundId != null && row.fundId !== ""
          ? String(row.fundId)
          : "";
    if (rowFund !== id) return acc;
    const a = parseFloat(row?.aum);
    return acc + (Number.isFinite(a) ? a : 0);
  }, 0);
}

/** @param {Record<string, unknown>} h */
function historicalAumsKeyMap(h) {
  const m = new Map();
  for (const [k, v] of Object.entries(h)) {
    m.set(String(k).toLowerCase().replace(/_/g, ""), v);
  }
  return m;
}

/** Read first matching numeric field (aliases differ by backend / casing). */
function readHistNum(m, ...aliasGroups) {
  for (const group of aliasGroups) {
    const keys = Array.isArray(group) ? group : [group];
    for (const raw of keys) {
      const lk = String(raw).toLowerCase().replace(/_/g, "");
      const v = m.get(lk);
      if (v !== undefined && v !== null && v !== "") {
        const x = Number(v);
        if (Number.isFinite(x)) return x;
      }
    }
  }
  return null;
}

/** @param {Record<string, unknown>|null|undefined} seamlessFundRow */
function getHistoricalAumsPayload(seamlessFundRow) {
  if (!seamlessFundRow || typeof seamlessFundRow !== "object") return null;
  const candidates = [
    seamlessFundRow.historicalAUMs,
    seamlessFundRow.HistoricalAUMs,
    seamlessFundRow.historicalAums,
  ];
  for (const h of candidates) {
    if (h == null) continue;
    if (typeof h === "string") {
      try {
        const p = JSON.parse(h);
        if (p && typeof p === "object" && !Array.isArray(p)) return p;
      } catch {
        /* ignore */
      }
      continue;
    }
    if (typeof h === "object" && !Array.isArray(h)) return h;
  }
  return null;
}

/**
 * Raw levels from `historicalAUMs` on a Seamless fund row (null bucket = missing key).
 * @param {Record<string, unknown>|null|undefined} seamlessFundRow
 * @returns {{
 *   present: number;
 *   weekToDate: number | null;
 *   monthToDate: number | null;
 *   quarterToDate: number | null;
 *   yearToDate: number | null;
 *   previousYear: number | null;
 *   rolling365: number | null;
 * } | null}
 */
export function readSeamlessFundHistoricalLevels(seamlessFundRow) {
  if (!seamlessFundRow || typeof seamlessFundRow !== "object") return null;
  const h = getHistoricalAumsPayload(seamlessFundRow);
  if (!h) return null;
  const m = historicalAumsKeyMap(h);

  let present = readHistNum(m, ["presentday", "present"]);
  if (present == null) {
    const a = Number(seamlessFundRow.aum);
    if (Number.isFinite(a)) present = a;
  }
  if (present == null || !Number.isFinite(present)) return null;

  return {
    present,
    weekToDate: readHistNum(m, ["weektodate", "weektodateaum"]),
    monthToDate: readHistNum(m, ["monthtodate", "monthtodateaum"]),
    quarterToDate: readHistNum(m, ["quartertodate", "quartertodateaum", "qtd", "quarterlytodate"]),
    yearToDate: readHistNum(m, ["yeartodate", "yeartodateaum", "ytd"]),
    previousYear: readHistNum(m, ["previousyear", "prioryear", "yearago", "lastyear"]),
    rolling365: readHistNum(m, [
      "rolling365todate",
      "365daystodate",
      "days365todate",
      "threesixtyfivedaytodate",
      "rollingyeartodate",
      "last365daystodate",
      "oneyeartodate",
    ]),
  };
}

/**
 * Seamless fund row (`accountingSystems` → seamless → `funds[]`):
 * AUM = PresentDay (from `historicalAUMs`, else top-level `aum`).
 * Week / month / quarter / year = PresentDay minus that bucket’s cumulative level (same field names as API).
 * 365 = PresentDay minus rolling-365 anchor when present; else PresentDay − PreviousYear when present.
 * @param {Record<string, unknown>|null|undefined} seamlessFundRow
 * @returns {{ aum: number; week: number; month: number; quarter: number; year: number; days365: number } | null}
 */
export function getSeamlessFundMetricsFromHistoricalAums(seamlessFundRow) {
  if (!seamlessFundRow || typeof seamlessFundRow !== "object") return null;
  const L = readSeamlessFundHistoricalLevels(seamlessFundRow);
  if (!L) {
    const a = Number(seamlessFundRow.aum);
    if (!Number.isFinite(a)) return null;
    return { aum: a, week: 0, month: 0, quarter: 0, year: 0, days365: 0 };
  }
  const present = L.present;
  const delta = (v) => (v != null && Number.isFinite(v) ? present - v : 0);

  let days365 = 0;
  if (L.rolling365 != null && Number.isFinite(L.rolling365)) days365 = present - L.rolling365;
  else if (L.previousYear != null && Number.isFinite(L.previousYear)) days365 = present - L.previousYear;

  return {
    aum: present,
    week: delta(L.weekToDate),
    month: delta(L.monthToDate),
    quarter: delta(L.quarterToDate),
    year: delta(L.yearToDate),
    days365,
  };
}

function dedupeSeamlessFundRowsByFundKey(seamlessFundRows) {
  const byFund = new Map();
  for (const row of seamlessFundRows) {
    if (!row || typeof row !== "object") continue;
    const fk =
      row.fund != null && row.fund !== ""
        ? String(row.fund)
        : row.fundId != null && row.fundId !== ""
          ? String(row.fundId)
          : "";
    if (!fk) continue;
    if (!byFund.has(fk)) byFund.set(fk, row);
  }
  return [...byFund.values()];
}

/**
 * Manager overview row when Seamless funds exist:
 * AUM = Σ PresentDay; each period column = Σ PresentDay − Σ (that period’s cumulative) across funds.
 * Missing bucket on a fund counts as 0 in the Σ(bucket) sum.
 */
function aggregateSeamlessFundRowsHistoricalMetrics(seamlessFundRows) {
  const z = { aum: 0, week: 0, month: 0, quarter: 0, year: 0, days365: 0 };
  if (!Array.isArray(seamlessFundRows) || seamlessFundRows.length === 0) return z;

  const rows = dedupeSeamlessFundRowsByFundKey(seamlessFundRows);
  let sumPresent = 0;
  let sumWeekToDate = 0;
  let sumMonthToDate = 0;
  let sumQuarterToDate = 0;
  let sumYearToDate = 0;
  let sumPreviousYear = 0;
  let sumRolling365 = 0;
  let anyRolling365 = false;
  let anyQuarterToDate = false;

  for (const row of rows) {
    const L = readSeamlessFundHistoricalLevels(row);
    if (!L) {
      const a = parseFloat(row?.aum);
      if (Number.isFinite(a)) sumPresent += a;
      continue;
    }
    sumPresent += L.present;
    sumWeekToDate += L.weekToDate ?? 0;
    sumMonthToDate += L.monthToDate ?? 0;
    if (L.quarterToDate != null && Number.isFinite(L.quarterToDate)) {
      anyQuarterToDate = true;
      sumQuarterToDate += L.quarterToDate;
    }
    sumYearToDate += L.yearToDate ?? 0;
    sumPreviousYear += L.previousYear ?? 0;
    if (L.rolling365 != null && Number.isFinite(L.rolling365)) {
      anyRolling365 = true;
      sumRolling365 += L.rolling365;
    }
  }

  const days365 = anyRolling365 ? sumPresent - sumRolling365 : sumPresent - sumPreviousYear;

  return {
    aum: sumPresent,
    week: sumPresent - sumWeekToDate,
    month: sumPresent - sumMonthToDate,
    quarter: anyQuarterToDate ? sumPresent - sumQuarterToDate : 0,
    year: sumPresent - sumYearToDate,
    days365,
  };
}

// --- lots ------------------------------------------------------------------

export function filterLotsByFundId(lots, entityId) {
  if (!Array.isArray(lots) || entityId == null || entityId === "") return [];
  const id = String(entityId);
  return lots.filter((lot) => String(lot?.fund ?? "") === id);
}

export function filterLotsForManager(lots, entityIds) {
  if (!Array.isArray(lots) || !entityIds?.size) return [];
  return lots.filter((lot) => {
    const fund = lot?.fund != null && lot?.fund !== "" ? String(lot.fund) : "";
    return entityIds.has(fund);
  });
}

export function countOpenLots(managerLots) {
  if (!Array.isArray(managerLots)) return 0;
  return managerLots.filter((lot) => Number(lot?.shareCount) !== 0).length;
}

export function lotNotionalUsd(lot) {
  const sh = Number(lot?.shareCount);
  const px = Number(lot?.sharePrice);
  if (!Number.isFinite(sh) || !Number.isFinite(px)) return 0;
  return Math.abs(sh * px);
}

// --- Seamless strategy children + lots-based rows (detail expand) ----------

function compareStrategyName(a, b) {
  return String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, { sensitivity: "base" });
}

/** Seamless fund row: strategy-level historical AUM payload (casing variants). */
function getStrategyHistoricalAumsObject(seamlessFundRow) {
  if (!seamlessFundRow || typeof seamlessFundRow !== "object") return null;
  const candidates = [seamlessFundRow.strategyHistoricalAUMs, seamlessFundRow.strategyHistoricalAums];
  for (const h of candidates) {
    if (h && typeof h === "object" && !Array.isArray(h)) return h;
  }
  return null;
}

/** Unique strategy ids that appear under this manager's Seamless funds (authoritative for "manager strategies"). */
function collectStrategyIdsFromSeamlessHistoricalRows(seamlessFundRows) {
  const ids = new Set();
  if (!Array.isArray(seamlessFundRows)) return ids;
  for (const row of seamlessFundRows) {
    const sh = getStrategyHistoricalAumsObject(row);
    if (!sh) continue;
    for (const sid of Object.keys(sh)) {
      if (sid) ids.add(String(sid));
    }
  }
  return ids;
}

/** Full strategy rows for the details dialog: from historical AUM keys when Seamless exists, else owner match. */
function buildStrategiesForManagerOverview(seamlessFundRows, ownerKey, sList) {
  if (Array.isArray(seamlessFundRows) && seamlessFundRows.length > 0) {
    const idSet = collectStrategyIdsFromSeamlessHistoricalRows(seamlessFundRows);
    const out = [];
    for (const sid of idSet) {
      const meta = Array.isArray(sList) ? sList.find((s) => String(s?.strategyId) === String(sid)) : null;
      const name = findStrategy(sid, sList) || sid || "—";
      out.push(meta ? { ...meta, name: meta.name ?? name } : { strategyId: sid, name });
    }
    out.sort(compareStrategyName);
    return out;
  }
  return Array.isArray(sList) ? sList.filter((s) => s?.owner === ownerKey) : [];
}

/** @param {Record<string, unknown>|null|undefined} hist */
function readHistoricalPnL(hist) {
  if (!hist || typeof hist !== "object") {
    return { week: 0, month: 0, quarter: 0, year: 0, days365: 0 };
  }
  const n = (k) => {
    const v = hist[k];
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };
  return {
    week: n("WeekToDate"),
    month: n("MonthToDate"),
    quarter: n("QuarterToDate"),
    year: n("YearToDate"),
    days365: n("PresentDay") !== 0 ? n("PresentDay") : n("YearToDate"),
  };
}

/**
 * Same period math as {@link getSeamlessFundMetricsFromHistoricalAums} (PresentDay − bucket / 365 rules)
 * when `strategyHistoricalAUMs[id]` matches fund `historicalAUMs` shape; otherwise legacy PascalCase fields.
 */
function metricsForSeamlessStrategyChild(histEntry, aumFromSau) {
  const synth =
    histEntry != null && histEntry !== ""
      ? { historicalAUMs: histEntry, aum: aumFromSau }
      : { aum: aumFromSau };
  const fromHist = getSeamlessFundMetricsFromHistoricalAums(synth);
  if (fromHist) {
    const aumN = Number(aumFromSau);
    return {
      aum: Number.isFinite(aumN) ? aumN : fromHist.aum,
      week: fromHist.week,
      month: fromHist.month,
      quarter: fromHist.quarter,
      year: fromHist.year,
      days365: fromHist.days365,
    };
  }
  const p = readHistoricalPnL(histEntry);
  const aumN = Number(aumFromSau);
  return {
    aum: Number.isFinite(aumN) ? aumN : 0,
    week: p.week,
    month: p.month,
    quarter: p.quarter,
    year: p.year,
    days365: p.days365,
  };
}

/**
 * Strategy rows under a fund from Seamless `strategyHistoricalAUMs` only (keys there = strategies with AUM for this fund).
 * `strategyAUMs` is used only for numeric AUM when present; `strategies` is for display names by id.
 * @param {Record<string, unknown>|null|undefined} seamlessFundRow
 * @param {unknown[]} strategies
 */
export function buildSeamlessStrategyChildRows(seamlessFundRow, strategies) {
  if (!seamlessFundRow || typeof seamlessFundRow !== "object") return [];
  const sau =
    seamlessFundRow.strategyAUMs && typeof seamlessFundRow.strategyAUMs === "object"
      ? seamlessFundRow.strategyAUMs
      : {};
  const sh = getStrategyHistoricalAumsObject(seamlessFundRow) ?? {};
  const ids = new Set(Object.keys(sh));
  const out = [];
  for (const sid of ids) {
    if (!sid) continue;
    const name = findStrategy(sid, strategies) || sid || "—";
    const aumRaw = sau[sid];
    const p = metricsForSeamlessStrategyChild(sh[sid], aumRaw);
    out.push({
      key: sid,
      name,
      aum: p.aum,
      week: p.week,
      month: p.month,
      quarter: p.quarter,
      year: p.year,
      days365: p.days365,
    });
  }
  out.sort(compareStrategyName);
  return out;
}

/** Roll up P/L across Seamless strategy children (fund-level columns). */
export function rollupSeamlessFundPnL(children) {
  return children.reduce(
    (acc, c) => ({
      week: acc.week + (Number(c.week) || 0),
      month: acc.month + (Number(c.month) || 0),
      quarter: acc.quarter + (Number(c.quarter) || 0),
      year: acc.year + (Number(c.year) || 0),
      days365: acc.days365 + (Number(c.days365) || 0),
    }),
    { week: 0, month: 0, quarter: 0, year: 0, days365: 0 }
  );
}


export function buildStrategyChildRows({
  entityId,
  lotsForManager,
  strategies,
  strategiesForManager: _strategiesForManager,
  accountingData,
  funds,
  livePrices,
  trades,
  markets,
  isStrategist,
  seamlessFundRow = null,
}) {
  const shObj = seamlessFundRow ? getStrategyHistoricalAumsObject(seamlessFundRow) : null;
  const hasSeamlessStrategies =
    seamlessFundRow &&
    typeof seamlessFundRow === "object" &&
    shObj &&
    Object.keys(shObj).length > 0;

  if (hasSeamlessStrategies) {
    return buildSeamlessStrategyChildRows(seamlessFundRow, strategies);
  }

  const lotsForEntity = filterLotsByFundId(lotsForManager, entityId);
  const pnlArgs = {
    accountingData: Array.isArray(accountingData) ? accountingData : [],
    funds: Array.isArray(funds) ? funds : [],
    strategies: Array.isArray(strategies) ? strategies : [],
    marketData: Array.isArray(livePrices) ? livePrices : [],
    trades: Array.isArray(trades) ? trades : [],
    isStrategist: Boolean(isStrategist),
    markets: Array.isArray(markets) ? markets : [],
  };

  const byStrat = new Map();
  for (const lot of lotsForEntity) {
    const sid = lot?.strategy != null && lot?.strategy !== "" ? String(lot.strategy) : "";
    const key = sid || "__none__";
    if (!byStrat.has(key)) byStrat.set(key, []);
    byStrat.get(key).push(lot);
  }

  const fromLots = [];
  for (const [sid, ls] of byStrat) {
    const name = sid === "__none__" ? "Unassigned" : findStrategy(sid, strategies) || sid || "—";
    const aum = ls.reduce((acc, lot) => acc + lotNotionalUsd(lot), 0);
    const pnL = aggregateManagerPeriodPnL({ ...pnlArgs, managerLots: ls });
    fromLots.push({
      key: sid || "__none__",
      name,
      aum,
      week: pnL.week,
      month: pnL.month,
      quarter: pnL.quarter,
      year: pnL.year,
      days365: pnL.days365,
    });
  }
  fromLots.sort(compareStrategyName);
  return fromLots;
}

// --- fund / SMA entry lists (dialog) ---------------------------------------

function buildFundEntries(manager, fundsList) {
  const fds = manager?.funds;
  if (!fds) return [];
  const entries = Array.isArray(fds)
    ? fds.map((data, i) => ({ key: `fund-${i}`, id: extractFundId(data, i), data }))
    : Object.entries(fds).map(([k, data]) => ({ key: k, id: k, data }));
  return [...entries].sort((x, y) =>
    compareLabelInsensitive(fundLabel(x.id, x.data, fundsList), fundLabel(y.id, y.data, fundsList))
  );
}

function buildSmaEntries(manager) {
  const smas = manager?.smas;
  if (!smas) return [];
  const entries = Array.isArray(smas)
    ? smas.map((data, i) => ({ key: `sma-${i}`, id: extractSmaId(data, i), data }))
    : Object.entries(smas).map(([k, data]) => ({ key: k, id: k, data }));
  return [...entries].sort((x, y) =>
    compareLabelInsensitive(smaLabel(x.id, x.data), smaLabel(y.id, y.data))
  );
}

// --- build rows (table + details) -------------------------------------------

function buildOneManagerOverviewRow(manager, ctx) {
  const {
    strategies: sList,
    lots: lList,
    accountingRowsAll,
    accountingData,
    funds: fList,
    livePrices,
    trades,
    markets,
    isStrategist,
  } = ctx;
  const m = manager;
  const id = m?.managerId;
  const ownerKey = id ? `manager#${id}` : "";
  const { fundIds, allEntityIds } = collectManagerEntityIds(m);
  const accountingRows = getAccountingRowsForManagerOverview(m, accountingRowsAll, id);
  const seamlessFundRows = getSeamlessFundRowsForManager(m?.accountingSystems, id, fundIds);
  const strategiesForManager = buildStrategiesForManagerOverview(seamlessFundRows, ownerKey, sList);
  const strategyCount = strategiesForManager.length;
  const seamlessHistAgg = aggregateSeamlessFundRowsHistoricalMetrics(seamlessFundRows);

  const lotsForManager = filterLotsForManager(lList, allEntityIds);
  const lotCount = lotsForManager.length;
  const openLotCount = countOpenLots(lotsForManager);

  const pnLFromLots = aggregateManagerPeriodPnL({
    managerLots: lotsForManager,
    accountingData: Array.isArray(accountingData) ? accountingData : [],
    funds: fList,
    strategies: sList,
    marketData: Array.isArray(livePrices) ? livePrices : [],
    trades: Array.isArray(trades) ? trades : [],
    isStrategist: Boolean(isStrategist),
    markets: Array.isArray(markets) ? markets : [],
  });

  let aum;
  let week;
  let month;
  let quarter;
  let year;
  let days365;
  if (seamlessFundRows.length > 0) {
    aum = seamlessHistAgg.aum;
    week = seamlessHistAgg.week;
    month = seamlessHistAgg.month;
    quarter = seamlessHistAgg.quarter;
    year = seamlessHistAgg.year;
    days365 = seamlessHistAgg.days365;
  } else {
    aum = sumAumFromRows(accountingRows);
    week = pnLFromLots.week;
    month = pnLFromLots.month;
    quarter = pnLFromLots.quarter;
    year = pnLFromLots.year;
    days365 = pnLFromLots.days365;
  }

  const fundEntries = buildFundEntries(m, fList);
  const smaEntries = buildSmaEntries(m);
  const seamlessFundById = mapSeamlessFundRowsByFundId(m?.accountingSystems, id, fundIds);

  const pnlBase = {
    accountingData: Array.isArray(accountingData) ? accountingData : [],
    funds: fList,
    strategies: sList,
    marketData: Array.isArray(livePrices) ? livePrices : [],
    trades: Array.isArray(trades) ? trades : [],
    isStrategist: Boolean(isStrategist),
    markets: Array.isArray(markets) ? markets : [],
  };

  const fundSmaMetrics = { funds: new Map(), smas: new Map() };

  for (const e of fundEntries) {
    const le = filterLotsByFundId(lotsForManager, e.id);
    const seamlessRow = seamlessFundById.get(String(e.id));
    const aumAcct = sumAumForEntity(accountingRows, e.id);
    const aumLots = le.reduce((acc, lot) => acc + lotNotionalUsd(lot), 0);
    const pnLRow = aggregateManagerPeriodPnL({ ...pnlBase, managerLots: le });

    const histM = seamlessRow ? getSeamlessFundMetricsFromHistoricalAums(seamlessRow) : null;
    if (histM) {
      fundSmaMetrics.funds.set(e.key, histM);
      continue;
    }

    if (seamlessRow && typeof seamlessRow === "object") {
      const seamlessAum = Number(seamlessRow.aum);
      const children = buildSeamlessStrategyChildRows(seamlessRow, sList);
      if (children.length > 0) {
        const rollup = rollupSeamlessFundPnL(children);
        fundSmaMetrics.funds.set(e.key, {
          aum: Number.isFinite(seamlessAum) ? seamlessAum : aumAcct || aumLots,
          ...rollup,
        });
      } else {
        fundSmaMetrics.funds.set(e.key, {
          aum: Number.isFinite(seamlessAum) ? seamlessAum : aumAcct || aumLots,
          ...pnLRow,
        });
      }
    } else {
      fundSmaMetrics.funds.set(e.key, { aum: aumAcct || aumLots, ...pnLRow });
    }
  }

  for (const e of smaEntries) {
    const le = filterLotsByFundId(lotsForManager, e.id);
    const aumAcct = sumAumForEntity(accountingRows, e.id);
    const aumLots = le.reduce((acc, lot) => acc + lotNotionalUsd(lot), 0);
    const pnLRow = aggregateManagerPeriodPnL({ ...pnlBase, managerLots: le });
    fundSmaMetrics.smas.set(e.key, { aum: aumAcct || aumLots, ...pnLRow });
  }

  const strategyChildrenMaps = { funds: new Map(), smas: new Map() };
  const childArgs = {
    lotsForManager,
    strategies: sList,
    strategiesForManager,
    accountingData,
    funds: fList,
    livePrices,
    trades,
    markets,
    isStrategist: Boolean(isStrategist),
  };

  for (const e of fundEntries) {
    const seamlessRow = seamlessFundById.get(String(e.id)) ?? null;
    strategyChildrenMaps.funds.set(
      e.key,
      buildStrategyChildRows({
        ...childArgs,
        entityId: e.id,
        seamlessFundRow: seamlessRow,
      })
    );
  }
  for (const e of smaEntries) {
    strategyChildrenMaps.smas.set(
      e.key,
      buildStrategyChildRows({
        ...childArgs,
        entityId: e.id,
        seamlessFundRow: null,
      })
    );
  }

  return {
    name: m?.name || m?.owner || "Manager",
    type: m?.type != null && m?.type !== "" ? String(m.type) : "—",
    managerId: id,
    aum,
    week,
    month,
    quarter,
    year,
    days365,
    fundCount: countFundsShape(m),
    smaCount: countSmasShape(m),
    strategyCount,
    lotCount,
    openLotCount,
    details: {
      manager: m,
      strategiesForManager,
      lotsForManager,
      fundEntries,
      smaEntries,
      fundSmaMetrics,
      strategyChildrenMaps,
      accountingRows,
    },
  };
}

/**
 * One array: each item has manager **table** fields (`name`, `aum`, counts, `week`, …) and `details` for the dialog.
 */
export function buildManagersOverviewRows({
  managers,
  strategies,
  lots,
  accountingRowsAll,
  accountingData,
  funds,
  livePrices,
  trades,
  markets,
  isStrategist,
}) {
  const mList = [...(Array.isArray(managers) ? managers : [])].sort((a, b) => {
    const na = a?.name || a?.owner || "Manager";
    const nb = b?.name || b?.owner || "Manager";
    return na.localeCompare(nb, undefined, { sensitivity: "base" });
  });
  const ctx = {
    strategies,
    lots,
    accountingRowsAll,
    accountingData,
    funds,
    livePrices,
    trades,
    markets,
    isStrategist,
  };
  return mList.map((manager) => buildOneManagerOverviewRow(manager, ctx));
}
